import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const NAMESPACES = ["common", "auth", "landing", "app", "billing", "profile", "settings", "support", "notifications"] as const;

const localeModules = import.meta.glob("./locales/*/*.json", { eager: true }) as Record<string, { default?: Record<string, unknown> }>;

function discoverLanguages(): string[] {
  const langs = new Set<string>();
  for (const path of Object.keys(localeModules)) {
    const match = path.match(/\.\/locales\/([^/]+)\//);
    if (match) langs.add(match[1]);
  }
  return Array.from(langs);
}

function buildResources(): Record<string, Record<string, Record<string, unknown>>> {
  const resources: Record<string, Record<string, Record<string, unknown>>> = {};
  const langs = discoverLanguages();
  for (const lang of langs) {
    resources[lang] = {};
    for (const ns of NAMESPACES) {
      const key = `./locales/${lang}/${ns}.json`;
      const mod = localeModules[key];
      if (mod) {
        resources[lang][ns] = (mod.default ?? mod) as Record<string, unknown>;
      }
    }
  }
  return resources;
}

const BASE = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");

export function detectLangFromPath(): string | null {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  const stripped = BASE && path.startsWith(BASE) ? path.slice(BASE.length) : path;
  if (stripped === "/es" || stripped.startsWith("/es/")) return "es";
  return null;
}

export function stripLangFromPath(path: string): string {
  const stripped = BASE && path.startsWith(BASE) ? path.slice(BASE.length) : path;
  if (stripped === "/es") return BASE || "/";
  if (stripped.startsWith("/es/")) return `${BASE}${stripped.slice(3)}`;
  return path;
}

export function withLangPath(path: string, lang: string): string {
  const stripped = stripLangFromPath(path);
  const rel = BASE && stripped.startsWith(BASE) ? stripped.slice(BASE.length) || "/" : stripped;
  if (lang === "es") {
    if (rel === "/") return `${BASE}/es/`;
    return `${BASE}/es${rel}`;
  }
  return `${BASE}${rel === "/" ? "/" : rel}`;
}

const discoveredLangs = discoverLanguages();

const detector = new LanguageDetector();
detector.addDetector({
  name: "pathPrefix",
  lookup() {
    return detectLangFromPath() ?? undefined;
  },
  cacheUserLanguage() {
    /* noop — path is the source of truth */
  },
});

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: buildResources(),
    fallbackLng: "en",
    supportedLngs: discoveredLangs,
    ns: [...NAMESPACES],
    defaultNS: "common",
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["pathPrefix", "querystring", "localStorage", "navigator"],
      lookupQuerystring: "lang",
      lookupLocalStorage: "viralens_lang",
      caches: ["localStorage"],
    },
    react: {
      useSuspense: false,
    },
  });

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

if (i18n.language) {
  document.documentElement.lang = i18n.language;
}

export default i18n;
